import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import NoteBox from '@repo/ui/note-box'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import useTranslate from '../../../../lib/hooks/use-translate'
import {
  DeactivationReasonEnum,
  hostFamilyDeactivationReasonEnumMapping
} from '../../../../lib/types/enums/staff/deactivation-reason.enum'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { createNumberArraySchema } from '../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type HostFamilyDeactivationInput = {
  ids: number[]
  deactivationReason: DeactivationReasonEnum
}

const HostFamilyDeactivationFormZodSchema: ZodType<HostFamilyDeactivationInput> = z.object({
  ids: createNumberArraySchema('ids'),
  deactivationReason: createEnumSchema({
    fieldName: 'raison',
    enumType: DeactivationReasonEnum
  })
})

type Props = BaseFormProps<HostFamilyDeactivationInput, true> & {
  reasons?: DeactivationReasonEnum[]
}

export const HostFamilyDeactivationForm = ({
  defaultValues,
  reasons = Object.values(DeactivationReasonEnum),
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()

  const form = useForm<HostFamilyDeactivationInput>({
    resolver: zodResolver(HostFamilyDeactivationFormZodSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'flex flex-col gap-5 px-4 pt-4'}>
          <FormField
            control={form.control}
            name={'deactivationReason'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label={t('host.family.forms.deactivation.placeholder')}
                    options={reasons.map((reason) => ({
                      label: t(hostFamilyDeactivationReasonEnumMapping[reason]),
                      value: reason
                    }))}
                    selectedOption={getSelectedEnumOption(
                      field.value,
                      hostFamilyDeactivationReasonEnumMapping,
                      t
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <NoteBox
            note={t('host.family.forms.deactivation.info.message')}
            className={'bg-red-light'}
            color={tailwindColors.red.DEFAULT}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}
